import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createProductTypeAction,
  getProductTypesAction,
} from "../../../redux/actions/ProductTypeactions";
import LoaderLin from "../../layout/LoaderLin";
import {
  Container,
  Typography,
  TextField,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  IconButton,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Divider,
  Chip,
  Stack,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import SearchIcon from "@mui/icons-material/Search";

const ProductTypeComponent = () => {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const productTypesList = useSelector((state) => state.productTypes);
  const { loading, error, productTypes } = productTypesList;

  const [newProductType, setNewProductType] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    dispatch(getProductTypesAction(userInfo.token));
  }, [dispatch]);

  const createProductTypeHandler = async (e) => {
    e.preventDefault();
    await dispatch(createProductTypeAction({ name: newProductType }, userInfo.token));
    setNewProductType("");
    setSnackbarMessage("Product type added successfully!");
    setSnackbarOpen(true);
    dispatch(getProductTypesAction(userInfo.token));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  console.log(productTypes);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          mb: 4,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 4, color: "white" }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <CategoryIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Product Types Management
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Manage and organize your product categories efficiently
              </Typography>
            </Box>
          </Stack>
          
          <Stack direction="row" spacing={3} mt={3}>
            <Chip
              label={`${Array.isArray(productTypes) ? productTypes.length : 0} Types`}
              variant="filled"
              sx={{ 
                bgcolor: "rgba(255,255,255,0.2)", 
                color: "white",
                fontWeight: "bold" 
              }}
            />
            <Chip
              label="Active"
              variant="filled"
              sx={{ 
                bgcolor: "rgba(76, 175, 80, 0.8)", 
                color: "white",
                fontWeight: "bold" 
              }}
            />
          </Stack>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" mb={4}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
            <LoaderLin />
          </Paper>
        </Box>
      ) : error ? (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            fontSize: "1.1rem"
          }}
        >
          {error}
        </Alert>
      ) : (
        <>
          {/* Add New Product Type Section */}
          <Card 
            elevation={3} 
            sx={{ 
              mb: 4, 
              borderRadius: 3,
              background: "linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)"
            }}
          >
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AddIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Add New Product Type
                  </Typography>
                </Stack>
              }
              sx={{ pb: 1 }}
            />
            <CardContent>
              <form onSubmit={createProductTypeHandler}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="end">
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Product Type Name"
                    value={newProductType}
                    onChange={(e) => setNewProductType(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                    placeholder="Enter product type name..."
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<AddIcon />}
                    sx={{
                      minWidth: 200,
                      height: 56,
                      borderRadius: 2,
                      background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                      boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                      },
                    }}
                  >
                    Add Type
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {/* Product Types Table Section */}
          <Card elevation={3} sx={{ borderRadius: 3 }}>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SearchIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Product Types List
                  </Typography>
                </Stack>
              }
              action={
                <Chip
                  label={`${Array.isArray(productTypes) ? productTypes.length : 0} items`}
                  color="primary"
                  variant="outlined"
                />
              }
            />
            <Divider />
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell 
                      sx={{ 
                        fontWeight: "bold", 
                        fontSize: "1rem",
                        color: "text.secondary"
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: "bold", 
                        fontSize: "1rem",
                        color: "text.secondary"
                      }}
                    >
                      Product Type Name
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontWeight: "bold", 
                        fontSize: "1rem",
                        color: "text.secondary"
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(productTypes) &&
                    productTypes?.map((type, index) => (
                      <TableRow 
                        key={type._id}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                          "&:nth-of-type(odd)": {
                            bgcolor: "grey.25",
                          },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={`#${String(index + 1).padStart(3, '0')}`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: "success.main",
                              }}
                            />
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{ fontSize: "1.1rem" }}
                            >
                              {type.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                       
                            <IconButton
                              color="error"
                              aria-label="delete"
                              sx={{
                                bgcolor: "error.50",
                                "&:hover": {
                                  bgcolor: "error.100",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {(!productTypes || productTypes.length === 0) && (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <CategoryIcon sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Product Types Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start by adding your first product type above
                </Typography>
              </Box>
            )}
          </Card>
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductTypeComponent;